import React, { useEffect, useRef, useState } from 'react'
import L from 'leaflet'
import 'leaflet/dist/leaflet.css'
import { adminService } from '../../../services/admin'
import { apiErrorMessage } from '../../../services/access'
import type { ShiftHistoryDetail, ShiftHistorySummary, ShiftHistoryTrail } from '../../../types/shiftHistory'
import { useI18n } from '../../../i18n/I18nContext'
import { EmptyState, ErrorBanner, LoadingState, PaginationControls } from './AdminUiPrimitives'
import { hasRecordedShiftTrail, orderedShiftHistoryTimeline, validShiftHistoryPoint } from './shiftHistoryViewModel'

type Props = {
  locationId?: string
  workerId?: string
  jobShiftId?: string
  assignmentId?: string
  initialShiftGroupId?: string
  surface?: 'admin' | 'supervisor'
}

const isoStart = (daysAgo: number) => {
  const date = new Date()
  date.setUTCDate(date.getUTCDate() - daysAgo)
  date.setUTCHours(0, 0, 0, 0)
  return date.toISOString()
}

export const ShiftHistoryViewer: React.FC<Props> = ({
  locationId,
  workerId,
  jobShiftId,
  assignmentId,
  initialShiftGroupId,
  surface = 'admin',
}) => {
  const { t, formatDateTime } = useI18n()
  const mapElement = useRef<HTMLDivElement | null>(null)
  const map = useRef<L.Map | null>(null)
  const layers = useRef<L.FeatureGroup | null>(null)
  const [items, setItems] = useState<ShiftHistorySummary[]>([])
  const [selectedId, setSelectedId] = useState(initialShiftGroupId || '')
  const [detail, setDetail] = useState<ShiftHistoryDetail | null>(null)
  const [trail, setTrail] = useState<ShiftHistoryTrail | null>(null)
  const [from, setFrom] = useState(isoStart(30))
  const [to, setTo] = useState(new Date().toISOString())
  const [status, setStatus] = useState('')
  const [hasTrail, setHasTrail] = useState('')
  const [hasException, setHasException] = useState('')
  const [selectedSegmentId, setSelectedSegmentId] = useState('')
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(25)
  const [pagination, setPagination] = useState({ page: 1, pageSize: 25, total: 0, totalPages: 0, hasNextPage: false, hasPreviousPage: false })
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')

  useEffect(() => {
    let active = true
    setLoading(true)
    setError('')
    adminService.listShiftHistory({
      from,
      to,
      locationId,
      workerId,
      jobShiftId,
      assignmentId,
      status: status || undefined,
      hasTrail: hasTrail || undefined,
      hasPunchException: hasException || undefined,
      page,
      pageSize,
    }, surface).then((response) => {
      if (!active) return
      const nextItems = response.data?.items || response.data?.rows || []
      setItems(nextItems)
      setPagination((current) => response.data?.pagination || current)
      setSelectedId((current) => nextItems.some((item: ShiftHistorySummary) => item.shiftGroupId === current) ? current : nextItems[0]?.shiftGroupId || '')
    }).catch((cause) => {
      if (active) setError(apiErrorMessage(cause, t('Unable to load shift history.')))
    }).finally(() => {
      if (active) setLoading(false)
    })
    return () => { active = false }
  }, [from, to, locationId, workerId, jobShiftId, assignmentId, status, hasTrail, hasException, page, pageSize, surface])

  useEffect(() => {
    if (!selectedId) {
      setDetail(null)
      setTrail(null)
      return
    }
    let active = true
    setLoading(true)
    setError('')
    Promise.all([
      adminService.getShiftHistory(selectedId, { locationId }, surface),
      adminService.getShiftHistoryTrail(selectedId, { locationId }, surface),
    ]).then(([detailResponse, trailResponse]) => {
      if (!active) return
      setDetail(detailResponse.data.data)
      setTrail(trailResponse.data.data)
      setSelectedSegmentId('')
    }).catch((cause) => {
      if (active) setError(apiErrorMessage(cause, t('Unable to load this shift history.')))
    }).finally(() => {
      if (active) setLoading(false)
    })
    return () => { active = false }
  }, [selectedId, locationId, surface])

  useEffect(() => {
    if (!mapElement.current || map.current) return
    const instance = L.map(mapElement.current).setView([0, 0], 2)
    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      maxZoom: 19,
      attribution: '&copy; OpenStreetMap contributors',
    }).addTo(instance)
    const group = L.featureGroup().addTo(instance)
    map.current = instance
    layers.current = group
    return () => {
      instance.remove()
      map.current = null
      layers.current = null
    }
  }, [detail?.shift.shiftGroupId])

  useEffect(() => {
    const instance = map.current
    const group = layers.current
    if (!instance || !group) return
    group.clearLayers()
    if (!trail) return
    const colors = ['#0f766e', '#2563eb', '#7c3aed', '#c2410c']
    trail.segments.filter((segment) => !selectedSegmentId || segment.segmentId === selectedSegmentId).forEach((segment, index) => {
      const points = segment.points.filter((point) => validShiftHistoryPoint(point.latitude, point.longitude))
      if (points.length > 1) {
        group.addLayer(L.polyline(points.map((point) => [point.latitude, point.longitude]), { color: colors[index % colors.length], weight: 4, opacity: 0.82 }))
      }
      if (points.length) {
        const first = points[0]
        group.addLayer(L.circleMarker([first.latitude, first.longitude], { radius: 5, color: colors[index % colors.length] }).bindPopup(`${t('Tracking segment')} ${index + 1}<br>${formatDateTime(first.recordedAt)}`))
      }
    })
    trail.punchMarkers.filter((marker) => validShiftHistoryPoint(marker.latitude, marker.longitude)).forEach((marker) => {
      group.addLayer(L.circleMarker([Number(marker.latitude), Number(marker.longitude)], { radius: 7, color: '#111827', fillColor: '#f59e0b', fillOpacity: 1 }).bindPopup(`${t(marker.type.replace(/_/g, ' '))}<br>${formatDateTime(marker.occurredAt)}`))
    })
    if (trail.location && validShiftHistoryPoint(trail.location.latitude, trail.location.longitude)) {
      group.addLayer(L.circleMarker([trail.location.latitude, trail.location.longitude], { radius: 6, color: '#166534' }).bindPopup(t('Scheduled worksite')))
      if (trail.location.radiusMeters) group.addLayer(L.circle([trail.location.latitude, trail.location.longitude], { radius: trail.location.radiusMeters, color: '#16a34a', fillOpacity: 0.08 }))
    }
    const bounds = group.getBounds()
    if (bounds.isValid()) instance.fitBounds(bounds.pad(0.15), { maxZoom: 17 })
    setTimeout(() => instance.invalidateSize(), 0)
  }, [trail, selectedSegmentId, t, formatDateTime])

  const fitTrail = () => {
    const instance = map.current
    const bounds = layers.current?.getBounds()
    if (instance && bounds?.isValid()) instance.fitBounds(bounds.pad(0.15), { maxZoom: 17 })
  }

  return (
    <div className="stack" data-testid="shift-history-viewer">
      <div className="admin-form-grid">
        <label>{t('From')}<input className="input" type="datetime-local" value={from.slice(0, 16)} onChange={(event) => {
          if (event.target.value) setFrom(new Date(event.target.value).toISOString())
        }} /></label>
        <label>{t('To')}<input className="input" type="datetime-local" value={to.slice(0, 16)} onChange={(event) => {
          if (event.target.value) setTo(new Date(event.target.value).toISOString())
        }} /></label>
        <label>{t('Status')}<select className="input" value={status} onChange={(event) => { setStatus(event.target.value); setPage(1) }}>
          <option value="">{t('All statuses')}</option>
          {['scheduled', 'active', 'on break', 'completed', 'missing punch-out', 'disputed/review', 'cancelled'].map((value) => <option key={value} value={value}>{t(value)}</option>)}
        </select></label>
        <label>{t('GPS trail')}<select className="input" value={hasTrail} onChange={(event) => { setHasTrail(event.target.value); setPage(1) }}>
          <option value="">{t('With or without trail')}</option><option value="true">{t('Has trail')}</option><option value="false">{t('No trail')}</option>
        </select></label>
        <label>{t('Punch exception')}<select className="input" value={hasException} onChange={(event) => { setHasException(event.target.value); setPage(1) }}>
          <option value="">{t('With or without exception')}</option><option value="true">{t('Has exception')}</option><option value="false">{t('No exception')}</option>
        </select></label>
      </div>
      <ErrorBanner message={error} />
      {loading && items.length === 0 ? <LoadingState label={t('Loading shift history...')} /> : null}
      {!loading && items.length === 0 ? <EmptyState title={t('No shifts matched')} description={t('Try widening the date range or clearing filters. Historical shifts remain available after punch-out and tracking completion.')} /> : null}
      {items.length ? (
        <div className="admin-table-wrapper">
          <table className="admin-table" aria-label={t('Shift history')}>
            <thead><tr><th>{t('Worker')}</th><th>{t('Scheduled')}</th><th>{t('Actual')}</th><th>{t('Status')}</th><th>{t('Evidence')}</th><th /></tr></thead>
            <tbody>{items.map((item) => (
              <tr key={item.shiftGroupId} className={selectedId === item.shiftGroupId ? 'is-selected' : ''}>
                <td>{item.worker.displayName}<br /><span className="helper">{item.job?.title || item.location?.name || t('Unassigned shift')}</span></td>
                <td>{item.scheduledShift ? `${formatDateTime(item.scheduledShift.startsAt)} – ${formatDateTime(item.scheduledShift.endsAt)}` : t('Legacy / unscheduled')}</td>
                <td>{item.actual.firstPunchInAt ? `${formatDateTime(item.actual.firstPunchInAt)} – ${item.actual.lastPunchOutAt ? formatDateTime(item.actual.lastPunchOutAt) : t('Missing punch-out')}` : t('No punches')}</td>
                <td><span className="status-badge">{t(item.status)}</span></td>
                <td>{t('{punches} punches · {points} GPS points · {segments} tracking segments', { punches: item.counts.punchEvents, points: item.counts.breadcrumbs + item.counts.workerLocationEvents, segments: item.counts.trackingSessions })}</td>
                <td><button type="button" className="btn btn-ghost" onClick={() => setSelectedId(item.shiftGroupId)}>{t('Open history')}</button></td>
              </tr>
            ))}</tbody>
          </table>
          <PaginationControls pagination={pagination} onPageChange={setPage} onPageSizeChange={(value) => { setPageSize(value); setPage(1) }} />
        </div>
      ) : null}
      {detail ? (
        <section className="panel workspace-panel stack" data-testid="shift-history-detail">
          <div className="admin-panel-toolbar">
            <div><h3>{detail.shift.worker.displayName}</h3><p className="helper">{detail.shift.job?.title || detail.shift.location?.name || t('Operational history')}</p></div>
            <span className="status-badge">{t(detail.shift.status)}</span>
          </div>
          {detail.shift.groupingSource === 'legacy_inferred' || detail.shift.diagnostics.some((value) => value.includes('Legacy')) ? <p className="admin-disabled-callout">{t('Some legacy events were grouped using a bounded worker, location, and shift window.')}</p> : null}
          {detail.shift.diagnostics.map((value) => <p key={value} className="admin-disabled-callout">{t(value)}</p>)}
          {(trail?.diagnostics || []).filter((value) => !detail.shift.diagnostics.includes(value)).map((value) => <p key={value} className="admin-disabled-callout">{t(value)}</p>)}
          <div className="inline-group">
            <label>{t('Tracking segment')}<select className="input" value={selectedSegmentId} onChange={(event) => setSelectedSegmentId(event.target.value)}>
              <option value="">{t('All segments')}</option>
              {(trail?.segments || []).map((segment, index) => <option key={segment.segmentId} value={segment.segmentId}>{t('Segment')} {index + 1} · {segment.points.length} {t('points')}</option>)}
            </select></label>
            <button type="button" className="btn btn-secondary" onClick={fitTrail} disabled={!hasRecordedShiftTrail(trail)}>{t('Fit trail')}</button>
          </div>
          <div ref={mapElement} style={{ height: 360, minHeight: 280, borderRadius: 12, overflow: 'hidden' }} aria-label={t('Historical GPS trail map')} />
          {!hasRecordedShiftTrail(trail) ? <EmptyState title={t('No GPS trail was recorded for this shift')} description={t('Missing breadcrumbs do not mean the worker was absent. Punch and schedule evidence remains visible below.')} /> : null}
          {trail && trail.segments.length > 1 ? <div className="admin-chip-list">{trail.segments.map((segment, index) => <span className="admin-chip" key={segment.segmentId}>{t('Tracking segment')} {index + 1}: {segment.points.length} {t('points')}</span>)}</div> : null}
          <ol className="stack" aria-label={t('Shift operational timeline')}>
            {orderedShiftHistoryTimeline(detail.timeline).map((event) => <li key={event.id}><strong>{t(event.label)}</strong> <span className="helper">{formatDateTime(event.occurredAt)} · {event.source}</span>{event.detail ? <div>{event.detail}</div> : null}</li>)}
          </ol>
        </section>
      ) : null}
    </div>
  )
}
